import React, { useState, useEffect } from "react";
import { Card, Input, Button, message } from "antd";
import { useQuery, useMutation, gql } from "@apollo/client";
import "./thresholdsConfiguration.css";
import PerformanceThresholdBar from "../../../../Images/threshold1.png";
import ResidencyThresholdBar from "../../../../Images/threshold2.png";
import GradientColorBarLine1 from "../../../../Images/gradientColorsBarLine1.png";
import LessThanRedIcon from "../../../../Images/lessthanIconRed.png";
import EqualtoRedIcon from "../../../../Images/equalsToRedIcon.png";
import GreaterThenRedIcon from "../../../../Images/greaterThenRedIcon.png";
import { ADD_JOBROLE_METRIC_THRESHOLDS, GET_JOBROLE_METRIC_THRESHOLDS } from "../../../../services/userAPI";
import { useLocation } from "react-router-dom";

const ThresholdsConfiguration = ({ onSaveSuccess }) => {
  const [performanceMin, setPerformanceMin] = useState("");
  const [performanceMax, setPerformanceMax] = useState("");
  const [residencyMin, setResidencyMin] = useState("");
  const [residencyMax, setResidencyMax] = useState("");
  const [initialPerformanceMin, setInitialPerformanceMin] = useState(""); // Store initial values
  const [initialPerformanceMax, setInitialPerformanceMax] = useState("");
  const [initialResidencyMin, setInitialResidencyMin] = useState("");
  const [initialResidencyMax, setInitialResidencyMax] = useState("");
  const location = useLocation();
  const roleId = location.state?.roleId;

  // Apollo Client query hooks for Performance and Residency
  const { data: perfData, loading: perfLoading, error: perfError } = useQuery(GET_JOBROLE_METRIC_THRESHOLDS, {
    variables: { jobroleId: roleId, metricType: "PERFORMANCE" },
  });
  const { data: resData, loading: resLoading, error: resError } = useQuery(GET_JOBROLE_METRIC_THRESHOLDS, {
    variables: { jobroleId: roleId, metricType: "RESIDENCY" },
  });

  // Apollo Client mutation hook
  const [addJobroleMetricThresholds, { loading: mutationLoading }] = useMutation(ADD_JOBROLE_METRIC_THRESHOLDS);

  // Populate input fields with fetched data
  useEffect(() => {
    if (perfData && perfData.getJobroleMetricThresholds) {
      const levels = perfData.getJobroleMetricThresholds.levelValues;
      const lowLevel = levels.find((l) => l.level === "LOW");
      const mediumLevel = levels.find((l) => l.level === "MEDIUM");
      const newPerfMin = lowLevel ? lowLevel.rangeMax.toString() : "";
      const newPerfMax = mediumLevel ? mediumLevel.rangeMax.toString() : "";
      setPerformanceMin(newPerfMin);
      setInitialPerformanceMin(newPerfMin); // Store initial value
      setPerformanceMax(newPerfMax);
      setInitialPerformanceMax(newPerfMax);
    }
    if (resData && resData.getJobroleMetricThresholds) {
      const levels = resData.getJobroleMetricThresholds.levelValues;
      const lowLevel = levels.find((l) => l.level === "LOW");
      const mediumLevel = levels.find((l) => l.level === "MEDIUM");
      const newResMin = lowLevel ? lowLevel.rangeMax.toString() : "";
      const newResMax = mediumLevel ? mediumLevel.rangeMax.toString() : "";
      setResidencyMin(newResMin);
      setInitialResidencyMin(newResMin); // Store initial value
      setResidencyMax(newResMax);
      setInitialResidencyMax(newResMax);
    }
  }, [perfData, resData]);

  // Handle query errors
  useEffect(() => {
    if (perfError) {
      message.error(`Error fetching performance thresholds: ${perfError.message}`);
    }
    if (resError) {
      message.error(`Error fetching residency thresholds: ${resError.message}`);
    }
  }, [perfError, resError]);

  // Handle input change and allow only numbers
  const handleNumberInput = (setter) => (e) => {
    const value = e.target.value;
    // Allow empty string or valid numbers
    if (value === "" || /^\d*$/.test(value)) {
      setter(value === "" ? "" : Number(value));
    }
  };

  // Validate thresholds and execute mutation on save
  const handleSave = async () => {
    // Check for empty fields
    const perfErrors = [];
    const resErrors = [];

    if (performanceMin === "") {
      perfErrors.push("Performance minimum cannot be empty.");
    }
    if (performanceMax === "") {
      perfErrors.push("Performance maximum cannot be empty.");
    }
    if (residencyMin === "") {
      resErrors.push("Residency minimum cannot be empty.");
    }
    if (residencyMax === "") {
      resErrors.push("Residency maximum cannot be empty.");
    }

    // Display empty field errors
    if (perfErrors.length > 0) {
      message.error(perfErrors.join(" "));
      return;
    }
    if (resErrors.length > 0) {
      message.error(resErrors.join(" "));
      return;
    }

    // Convert to numbers for min/max validation
    const perfMin = Number(performanceMin);
    const perfMax = Number(performanceMax);
    const resMin = Number(residencyMin);
    const resMax = Number(residencyMax);

    // Check min/max relationships
    if (perfMin >= perfMax) {
      message.error("Performance minimum must be less than performance maximum.");
      return;
    }
    if (resMin >= resMax) {
      message.error("Residency minimum must be less than residency maximum.");
      return;
    }

    try {
      // Prepare Performance thresholds
      const performanceThresholds = {
        jobroleId: roleId,
        metricType: "PERFORMANCE",
        levelValues: [
          {
            level: "LOW",
            rangeMin: 0,
            rangeMax: perfMin,
          },
          {
            level: "MEDIUM",
            rangeMin: perfMin,
            rangeMax: perfMax,
          },
          {
            level: "HIGH",
            rangeMin: perfMax,
            rangeMax: perfMax * 10,
          },
        ],
      };

      // Prepare Residency thresholds
      const residencyThresholds = {
        jobroleId: roleId,
        metricType: "RESIDENCY",
        levelValues: [
          {
            level: "LOW",
            rangeMin: 0,
            rangeMax: resMin,
          },
          {
            level: "MEDIUM",
            rangeMin: resMin,
            rangeMax: resMax,
          },
          {
            level: "HIGH",
            rangeMin: resMax,
            rangeMax: resMax * 10,
          },
        ],
      };

      // Execute mutation for Performance with refetch
      const performanceResponse = await addJobroleMetricThresholds({
        variables: { thresholds: performanceThresholds },
        refetchQueries: [
          {
            query: GET_JOBROLE_METRIC_THRESHOLDS,
            variables: { jobroleId: roleId, metricType: "PERFORMANCE" },
          },
        ],
      });

      if (!performanceResponse.data.addJobroleMetricThresholds.success) {
        message.error(performanceResponse.data.addJobroleMetricThresholds.message);
        return;
      }

      const residencyResponse = await addJobroleMetricThresholds({
        variables: { thresholds: residencyThresholds },
        refetchQueries: [
          {
            query: GET_JOBROLE_METRIC_THRESHOLDS,
            variables: { jobroleId: roleId, metricType: "RESIDENCY" },
          },
        ],
      });

      if (!residencyResponse.data.addJobroleMetricThresholds.success) {
        message.error(residencyResponse.data.addJobroleMetricThresholds.message);
        return;
      }

      // Show success message (fields updated via refetch)
      message.success("Thresholds saved successfully!");
      if (onSaveSuccess) onSaveSuccess()
    } catch (error) {
      message.error(`Error saving thresholds: ${error.message}`);
    }
  };

  // Clear all input fields on cancel
  const handleCancel = () => {
    setPerformanceMin(initialPerformanceMin);
    setPerformanceMax(initialPerformanceMax);
    setResidencyMin(initialResidencyMin);
    setResidencyMax(initialResidencyMax);
    // message.info("Changes discarded successfully!");
  };

  // Show loading state while fetching data
  if (perfLoading || resLoading) {
    return <div>Loading thresholds...</div>;
  }

  return (
    <div>
      <Card className="search-card" bodyStyle={{ padding: "10px" }}>
        <div className="thresholds-label">
          <label>Thresholds</label>
        </div>

        {/* Performance Ranges Section */}
        <div>
          <label className="performance-ranges-threshold">Performance Ranges (In %)</label>
        </div>
        <div className="name-bar">
          <span>Low Performance</span>
          <span>Medium Performance</span>
          <span>High Performance</span>
        </div>
        <div className="gradient-wrapper">
          <img className="gradient-color-bar" src={PerformanceThresholdBar} alt="Performance threshold range" />
          <img className="gradient-color-bar-line1" src={GradientColorBarLine1} alt="Line 1" />
          <img className="gradient-color-bar-line2" src={GradientColorBarLine1} alt="Line 2" />
          <div className="threshold-inputs">
            <div className="threshold-item">
              <span className="minimum-range">minimum range</span>
              <div className="symbol-group">
                <span className="label-text">less than</span>
                <img src={LessThanRedIcon} className="lessthenRedIcon" />
              </div>
              <Input
                type="number"
                className="input-thresholds"
                value={performanceMin}
                onChange={handleNumberInput(setPerformanceMin)}
                onWheel={(e) => e.currentTarget.blur()}
                style={{ width: "60px", borderRadius: "1px" }}
                min={0}
              />
              <div className="equal-group">
                <span className="label-text-equal-to">equals to</span>
                <img src={EqualtoRedIcon} />
              </div>
            </div>
            <div className="threshold-item">
              <span className="medium-range">medium range</span>
            </div>
            <div className="threshold-item-less-than">
              <div className="symbol-group">
                <span className="label-text">less than</span>
                <img src={LessThanRedIcon} className="lessthenRedIcon" />
              </div>
              <Input
                type="number"
                className="input-thresholds"
                value={performanceMax}
                onChange={handleNumberInput(setPerformanceMax)}
                onWheel={(e) => e.currentTarget.blur()}
                style={{ width: "60px", borderRadius: "1px" }}
                min={0}
              />
              <div className="maximum-group">
                <span className="label-text-greater-than">greater than</span>
                <img src={GreaterThenRedIcon} className="greaterthenRedIcon" />
              </div>
              <span className="maximum-range">maximum range</span>
            </div>
          </div>
        </div>

        {/* Residency Ranges Section */}
        <div>
          <label className="performance-ranges-threshold">Residency Ranges (In Months)</label>
        </div>
        <div className="name-bar">
          <span>Low Residency</span>
          <span>Medium Residency</span>
          <span>High Residency</span>
        </div>
        <div className="gradient-wrapper">
          <img className="gradient-color-bar" src={ResidencyThresholdBar} alt="Residency threshold range" />
          <img className="gradient-color-bar-line1" src={GradientColorBarLine1} alt="Line 1" />
          <img className="gradient-color-bar-line2" src={GradientColorBarLine1} />
          <div className="threshold-inputs">
            <div className="threshold-item">
              <span className="minimum-range">minimum range</span>
              <div className="symbol-group">
                <span className="label-text">less than</span>
                <img src={LessThanRedIcon} className="lessthenRedIcon" />
              </div>
              <Input
                type="number"
                className="input-thresholds"
                value={residencyMin}
                onChange={handleNumberInput(setResidencyMin)}
                onWheel={(e) => e.currentTarget.blur()}
                style={{ width: "60px", borderRadius: "1px" }}
                min={0}
              />
              <div className="equal-group">
                <span className="label-text-equal-to">equals to</span>
                <img src={EqualtoRedIcon} />
              </div>
            </div>
            <div className="threshold-item">
              <span className="medium-range">medium range</span>
            </div>
            <div className="threshold-item-less-than">
              <div className="symbol-group">
                <span className="label-text">less than</span>
                <img src={LessThanRedIcon} className="lessthenRedIcon" />
              </div>
              <Input
                type="number"
                className="input-thresholds"
                value={residencyMax}
                onChange={handleNumberInput(setResidencyMax)}
                onWheel={(e) => e.currentTarget.blur()}
                style={{ width: "60px", borderRadius: "1px" }}
                min={0}
              />
              <div className="maximum-group">
                <span className="label-text-greater-than">greater than</span>
                <img src={GreaterThenRedIcon} className="greaterthenRedIcon" />
              </div>
              <span className="maximum-range">maximum range</span>
            </div>
          </div>
        </div>
        <div className="button-group-thresholds">
          <button
            type="default"
            className="cancel-button-thresholds"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="primary"
            className="save-button-thresholds"
            onClick={handleSave}
            disabled={mutationLoading}
            style={{ opacity: mutationLoading ? 0.7 : 1, cursor: mutationLoading ? 'not-allowed' : 'pointer' }}
          >
            {mutationLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Save'}
          </button>
        </div>
      </Card>

    </div>
  );
};

export default ThresholdsConfiguration;